import React from 'react';
import { type EntryPointComponent, graphql, usePreloadedQuery } from 'react-relay';
import type { srcVirtualAgentLandingQuery } from '@atlassian/jira-relay/src/__generated__/srcVirtualAgentLandingQuery.graphql';
import { APP_NAMES } from '@atlassian/jira-servicedesk-common/src/utils/app-names';
import { JsdGettingStartedPanelShouldRender } from '@atlassian/jira-servicedesk-getting-started-panel/src/async';
import { VirtualAgentPageWrapper } from '@atlassian/jira-servicedesk-settings-virtual-agent-common/src/ui/virtual-agent-page-wrapper';
import { VirtualAgentLanding } from '@atlassian/jira-servicedesk-settings-virtual-agent-landing/src/ui/index.tsx';
import ServiceDeskAppBase from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/app-base/index.tsx';
import { ServiceDeskDocumentTitle } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/document-title/index.tsx';
import AppContainer from '@atlassian/jira-spa-apps-project-settings-common/src/components/app-container';
import { ProjectSettingsController } from '@atlassian/jira-spa-apps-project-settings-common/src/components/routing-filter';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { usePathParam } from '@atlassian/react-resource-router';

const VirtualAgentLandingPage: EntryPointComponent<{ page: srcVirtualAgentLandingQuery }, {}> = ({
	queries: { page: pageQueryReference },
}) => {
	const [projectKey] = usePathParam('projectKey');

	const data = usePreloadedQuery<srcVirtualAgentLandingQuery>(
		graphql`
			query srcVirtualAgentLandingQuery(
				$cloudId: ID!
				$projectKey: String!
				$checkForAiEnabledFF: Boolean!
			) @preloadable {
				jira {
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) {
						...virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_projectRef
						...ui_servicedeskSettingsVirtualAgentLanding_VirtualAgentLanding_projectRef
						key
						name
					}
				}
				virtualAgent @required(action: THROW) {
					virtualAgentEntitlements(cloudId: $cloudId) @required(action: THROW) {
						...ui_servicedeskSettingsVirtualAgentLanding_VirtualAgentLanding_featuresRef
							@arguments(checkForAiEnabledFF: $checkForAiEnabledFF)
					}
				}
				...virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_featuresRef
					@arguments(cloudId: $cloudId, checkForAiEnabledFF: $checkForAiEnabledFF)
			}
		`,
		pageQueryReference,
	);

	return (
		<ProjectSettingsController projectKey={projectKey}>
			<MarkProductStart />
			<ServiceDeskAppBase appName={APP_NAMES.VIRTUAL_AGENT_LANDING}>
				<VirtualAgentPageWrapper
					allowMode="not-configured"
					featuresRef={data}
					projectRef={data.jira?.jiraProjectByKey ?? null}
				>
					{data.jira?.jiraProjectByKey != null && (
						<AppContainer projectKey={data.jira.jiraProjectByKey.key} baseUrl="">
							<ServiceDeskDocumentTitle
								projectName={data.jira.jiraProjectByKey.name}
								section="virtualAgentLanding"
							/>
							<VirtualAgentLanding
								featuresRef={data.virtualAgent.virtualAgentEntitlements}
								projectRef={data.jira.jiraProjectByKey}
							/>
						</AppContainer>
					)}
				</VirtualAgentPageWrapper>
			</ServiceDeskAppBase>
			<JsdGettingStartedPanelShouldRender />
		</ProjectSettingsController>
	);
};

export default VirtualAgentLandingPage;
